export type AdminUserItem = {
  id: number;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  role: "ADMIN" | "SALES" | "Customer";
  emailConfirmed: boolean;
  registeredOn: string | null;
  address: string;
  profilePicture: string;
};

export type AdminUsersResponse = {
  users: AdminUserItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalUsers: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
  summary: {
    totalUsers: number;
    admins: number;
    sales: number;
    customers: number;
  };
};

export type AdminUserInput = {
  firstName: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role: "ADMIN" | "SALES" | "Customer";
  password?: string;
  emailConfirmed?: boolean;
  address?: string;
  profilePicture?: string;
};

async function parseResponse(response: Response) {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.message || `Request failed: ${response.status}`);
  }

  return data;
}

export async function fetchAdminUsers(query: URLSearchParams) {
  const response = await fetch(`/api/admin/users?${query.toString()}`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as AdminUsersResponse;
}

export async function fetchAdminUser(userId: number) {
  const response = await fetch(`/api/admin/users/${userId}`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as AdminUserItem;
}

export async function createAdminUser(input: AdminUserInput) {
  const response = await fetch("/api/admin/users", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}

export async function updateAdminUser(userId: number, input: AdminUserInput) {
  const response = await fetch(`/api/admin/users/${userId}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}

export async function deleteAdminUser(userId: number) {
  const response = await fetch(`/api/admin/users/${userId}`, {
    method: "DELETE",
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return parseResponse(response);
}
